/**
 * Comprehensive translations for the Guest Portal
 * English and Arabic translations for all pages and components
 */

export const translations = {
  en: {
    // Common UI elements
    common: {
      loading: 'Loading…',
      retry: 'Try again',
      noData: 'No data available',
      add: 'Add',
      edit: 'Edit',
      delete: 'Delete',
      save: 'Save',
      cancel: 'Cancel',
      close: 'Close',
      view: 'View',
      download: 'Download',
      actions: 'Actions',
      status: 'Status',
      active: 'Active',
      inactive: 'Inactive',
      name: 'Name',
      description: 'Description',
      date: 'Date',
      time: 'Time',
      submit: 'Submit',
      confirm: 'Confirm',
      back: 'Back',
      next: 'Next',
      previous: 'Previous',
      yes: 'Yes',
      no: 'No',
      required: 'Required',
      optional: 'Optional',
      all: 'All',
      select: 'Select',
      error: 'Error',
      warning: 'Warning',
      info: 'Info',
      success: 'Success',
      english: 'English',
      arabic: 'العربية',
    },

    // App header and shell
    app: {
      brand: 'Support portal',
      logout: 'Sign out',
      footerMessage: 'If anything here looks wrong, reply to the email that sent you this link.',
      language: 'Language',
      theme: 'Theme',
    },

    // Landing page
    landing: {
      title: 'You need an invitation link',
      description: 'This portal opens from the link in the email we sent you. Links are personal, so please use your own rather than one forwarded from a colleague.',
      lostEmail: 'Lost the email? Reply to whoever you have been dealing with and they can send a new invitation.',
    },

    // Invitation page
    invitation: {
      welcome: 'Welcome',
      description: 'This link gives you a place to report issues to us and follow what happens next.',
      openPortal: 'Open my portal',
      opening: 'Opening…',
      alreadyOpen: 'This invitation is already open',
      alreadyOpenDesc: 'If you are on a new device or a private window, ask for a fresh invitation — for security, each link opens once.',
      expired: 'This invitation has expired',
      expiredDesc: 'Please ask for a new one.',
      unavailable: 'This invitation is not available',
      unavailableDesc: 'Please ask for a new one, and mention that the old link did not open.',
    },

    // Desks page (What do you need?)
    desks: {
      title: 'What do you need?',
      empty: 'There is nothing set up for you to report against yet. If you were expecting something here, reply to the email that sent you this link.',
      reportIssue: 'Report an issue',
      seeReported: 'See what I have already reported',
    },

    // Submit issue page
    submit: {
      title: 'Report an issue',
      titleLabel: 'Title',
      descriptionLabel: 'What happened?',
      kindLabel: 'Kind',
      severityLabel: 'How badly does it affect you?',
      environmentLabel: 'Where did it happen?',
      environmentPlaceholder: 'Browser, device or site',
      send: 'Send',
      sending: 'Sending…',
      backToAllDesks: 'All desks',
    },

    // Issues list page
    issues: {
      title: 'What you have reported',
      empty: 'Nothing yet.',
      reportSomething: 'Report something',
      seeShared: 'See what has been shared with you',
      truncatedMessage: 'Showing your most recent {count} reports. Reply to the email that sent you this link if you need older ones.',
    },

    // Issue detail page
    issueDetail: {
      messagesTitle: 'Messages',
      addMessage: 'Add a message',
      sendMessage: 'Send message',
      sending: 'Sending…',
      conversationClosed: 'This conversation has been closed. Reply to the email that sent you this link if you still need help.',
      reopenToComment: 'Open this issue again to add a message.',
      commentsDisabled: 'Messages are turned off for this desk.',
      yourAnswer: 'Send answer',
      thankYouAnswer: 'Thank you — we have your answer.',
      weNeedOne: 'We need one thing from you',
      askQuestion: 'Send answer',
      thankYouResolution: 'Thank you — we have recorded that you are happy with this.',
      resolved: 'We think this is resolved',
      needsReread: 'Open this issue again to confirm or reopen it.',
      confirmed: 'You confirmed this was resolved. Nothing further is needed.',
      reopenPending: 'You have asked us to look at this again. We will come back to you.',
      confirmResolved: 'Yes, this is resolved',
      confirmSaving: 'Saving…',
      notResolved: 'It is not resolved',
      notResolvedPlaceholder: 'What is still wrong?',
      askReopen: 'Ask to reopen',
      reopenSending: 'Sending…',
      thankYouReopen: 'Thank you — we have asked the team to look again.',
      backToIssues: 'All issues',
      you: 'You',
      ourTeam: 'Our team',
    },

    // Workspaces page
    workspaces: {
      title: 'Shared with you',
      empty: 'Nothing has been shared with you yet.',
    },

    // Workspace hub
    workspaceHub: {
      files: 'Files',
      filesEmpty: 'Nothing here.',
      approvals: 'Waiting for your response',
      approvalsEmpty: 'Nothing here.',
      forms: 'Forms to fill in',
      formsEmpty: 'Nothing here.',
      shared: 'Shared with you',
      sharedEmpty: 'Nothing here.',
      updates: 'Updates',
      updatesEmpty: 'Nothing here.',
      download: 'Download',
      preparing: 'Preparing…',
      viewOnly: 'View only',
      files_count: '{count} file{count_plural}',
      noFileAvailable: 'That package has no file to download right now.',
      confirmed: 'Confirmed',
      confirmSeen: 'Confirm you have seen this',
      backToWorkspaces: 'Shared with you',
    },

    // Approval page
    approval: {
      reference: 'Reference',
      respondBy: 'Please respond by {date}.',
      thankYou: 'Thank you — your response ({response}) has been sent.',
      closed: 'Closed',
      backToWorkspace: 'Back',
    },

    // Form page
    form: {
      titleLabel: 'Title',
      thankYou: 'Thank you — your answers have been sent.',
      fillIn: 'Please fill in: {fields}.',
      send: 'Send',
      sending: 'Sending…',
      choose: 'Choose…',
      backToWorkspace: 'Back',
    },

    // Quote/RFQ page
    quote: {
      sendQuote: 'Send a quote',
      title: 'Title',
      total: 'Total',
      currency: 'Currency',
      currencyPlaceholder: 'USD',
      notes: 'Notes',
      send: 'Send quote',
      sending: 'Sending…',
      titleRequired: 'Please give your quote a title.',
      thankYou: 'Thank you — your quote has been sent{reference}.',
      backToWorkspace: 'Back',
    },

    // Session ended / Not found
    ended: {
      title: 'Your session has ended',
      description: 'For security, access closes when you sign out or after a period of inactivity. Please open your invitation link again.',
      lostLink: 'If the link no longer works, ask for a new invitation.',
    },

    notFound: {
      title: 'We could not find that page',
      goBack: 'Go back',
    },

    // Error messages
    errors: {
      somethingWrong: 'Something went wrong on our side.',
      couldNotAccept: 'We could not open your access just now.',
      couldNotOpen: 'We could not open that.',
      couldNotOpenDesk: 'We could not open that desk.',
      couldNotSend: 'We could not send that just now.',
      couldNotDownload: 'That download could not be started.',
      couldNotConfirm: 'That could not be confirmed.',
      couldNotRespond: 'That could not be sent.',
      couldNotSubmitForm: 'That could not be sent.',
      couldNotSubmitQuote: 'That could not be sent.',
    },
  },

  ar: {
    // Common UI elements
    common: {
      loading: 'جارٍ التحميل…',
      retry: 'حاول مرة أخرى',
      noData: 'لا توجد بيانات',
      add: 'إضافة',
      edit: 'تحرير',
      delete: 'حذف',
      save: 'حفظ',
      cancel: 'إلغاء',
      close: 'إغلاق',
      view: 'عرض',
      download: 'تحميل',
      actions: 'الإجراءات',
      status: 'الحالة',
      active: 'نشط',
      inactive: 'غير نشط',
      name: 'الاسم',
      description: 'الوصف',
      date: 'التاريخ',
      time: 'الوقت',
      submit: 'إرسال',
      confirm: 'تأكيد',
      back: 'رجوع',
      next: 'التالي',
      previous: 'السابق',
      yes: 'نعم',
      no: 'لا',
      required: 'مطلوب',
      optional: 'اختياري',
      all: 'الكل',
      select: 'اختر',
      error: 'خطأ',
      warning: 'تحذير',
      info: 'معلومات',
      success: 'نجاح',
      english: 'English',
      arabic: 'العربية',
    },

    // App header and shell
    app: {
      brand: 'بوابة الدعم',
      logout: 'تسجيل الخروج',
      footerMessage: 'إذا بدا أن هناك خطأ ما هنا، فرد على البريد الإلكتروني الذي أرسل لك هذا الرابط.',
      language: 'اللغة',
      theme: 'المظهر',
    },

    // Landing page
    landing: {
      title: 'أنت بحاجة إلى رابط دعوة',
      description: 'تُفتح هذه البوابة من الرابط الموجود في البريد الإلكتروني الذي أرسلناه لك. الروابط شخصية، لذا يرجى استخدام الخاص بك بدلاً من رابط تم إعادة توجيهه من أحد زملائك.',
      lostEmail: 'هل فقدت البريد الإلكتروني؟ رد على الشخص الذي تتعامل معه وسيمكنه إرسال دعوة جديدة.',
    },

    // Invitation page
    invitation: {
      welcome: 'أهلا وسهلا',
      description: 'يعطيك هذا الرابط مكاناً لتقرير المشاكل لنا والمتابعة حول ما يحدث بعد ذلك.',
      openPortal: 'فتح بوابتي',
      opening: 'جارٍ الفتح…',
      alreadyOpen: 'تم فتح هذه الدعوة بالفعل',
      alreadyOpenDesc: 'إذا كنت على جهاز جديد أو نافذة خاصة، اطلب دعوة جديدة - لأسباب أمنية، يتم فتح كل رابط مرة واحدة فقط.',
      expired: 'انتهت صلاحية هذه الدعوة',
      expiredDesc: 'يرجى طلب واحدة جديدة.',
      unavailable: 'هذه الدعوة غير متوفرة',
      unavailableDesc: 'يرجى طلب واحدة جديدة، وذكر أن الرابط القديم لم يتم فتحه.',
    },

    // Desks page (What do you need?)
    desks: {
      title: 'ماذا تحتاج؟',
      empty: 'لم يتم إعداد أي شيء لك للإبلاغ عنه حتى الآن. إذا كنت تتوقع شيئاً ما هنا، فرد على البريد الإلكتروني الذي أرسل لك هذا الرابط.',
      reportIssue: 'الإبلاغ عن مشكلة',
      seeReported: 'انظر ما قمت بالإبلاغ عنه بالفعل',
    },

    // Submit issue page
    submit: {
      title: 'الإبلاغ عن مشكلة',
      titleLabel: 'العنوان',
      descriptionLabel: 'ما الذي حدث؟',
      kindLabel: 'النوع',
      severityLabel: 'كم تؤثر هذه المشكلة على عملك؟',
      environmentLabel: 'أين حدثت المشكلة؟',
      environmentPlaceholder: 'المتصفح أو الجهاز أو الموقع',
      send: 'إرسال',
      sending: 'جارٍ الإرسال…',
      backToAllDesks: 'جميع الأقسام',
    },

    // Issues list page
    issues: {
      title: 'ما قمت بالإبلاغ عنه',
      empty: 'لا شيء حتى الآن.',
      reportSomething: 'الإبلاغ عن شيء',
      seeShared: 'انظر ما تم مشاركته معك',
      truncatedMessage: 'عرض أحدث {count} تقرير من تقاريرك. رد على البريد الإلكتروني الذي أرسل لك هذا الرابط إذا كنت بحاجة إلى تقارير أقدم.',
    },

    // Issue detail page
    issueDetail: {
      messagesTitle: 'الرسائل',
      addMessage: 'إضافة رسالة',
      sendMessage: 'إرسال الرسالة',
      sending: 'جارٍ الإرسال…',
      conversationClosed: 'تم إغلاق هذه المحادثة. رد على البريد الإلكتروني الذي أرسل لك هذا الرابط إذا كنت لا تزال بحاجة إلى المساعدة.',
      reopenToComment: 'افتح هذه المشكلة مرة أخرى لإضافة رسالة.',
      commentsDisabled: 'تم تعطيل الرسائل لهذا القسم.',
      yourAnswer: 'إرسال الإجابة',
      thankYouAnswer: 'شكراً - لقد تلقينا إجابتك.',
      weNeedOne: 'نحتاج إلى شيء واحد منك',
      askQuestion: 'إرسال الإجابة',
      thankYouResolution: 'شكراً - لقد سجلنا أنك راضٍ عن هذا.',
      resolved: 'نعتقد أن هذا تم حله',
      needsReread: 'افتح هذه المشكلة مرة أخرى لتأكيد أو إعادة فتح.',
      confirmed: 'أكدت أن هذا تم حله. لا توجد حاجة لمزيد من الإجراءات.',
      reopenPending: 'لقد طلبت منا النظر في هذا مرة أخرى. سنعود إليك.',
      confirmResolved: 'نعم، تم حل هذا',
      confirmSaving: 'جارٍ الحفظ…',
      notResolved: 'لم يتم حل هذا',
      notResolvedPlaceholder: 'ما الذي لا يزال خاطئاً؟',
      askReopen: 'اطلب إعادة الفتح',
      reopenSending: 'جارٍ الإرسال…',
      thankYouReopen: 'شكراً - لقد طلبنا من الفريق النظر مرة أخرى.',
      backToIssues: 'جميع المشاكل',
      you: 'أنت',
      ourTeam: 'فريقنا',
    },

    // Workspaces page
    workspaces: {
      title: 'تم مشاركته معك',
      empty: 'لم يتم مشاركة أي شيء معك حتى الآن.',
    },

    // Workspace hub
    workspaceHub: {
      files: 'الملفات',
      filesEmpty: 'لا شيء هنا.',
      approvals: 'في انتظار ردك',
      approvalsEmpty: 'لا شيء هنا.',
      forms: 'نماذج للملء',
      formsEmpty: 'لا شيء هنا.',
      shared: 'تم مشاركته معك',
      sharedEmpty: 'لا شيء هنا.',
      updates: 'التحديثات',
      updatesEmpty: 'لا شيء هنا.',
      download: 'تحميل',
      preparing: 'جارٍ الإعداد…',
      viewOnly: 'عرض فقط',
      files_count: '{count} ملف{count_plural}',
      noFileAvailable: 'لا توجد ملفات متاحة للتحميل الآن.',
      confirmed: 'تم التأكيد',
      confirmSeen: 'أكد أنك رأيت هذا',
      backToWorkspaces: 'تم مشاركته معك',
    },

    // Approval page
    approval: {
      reference: 'المرجع',
      respondBy: 'يرجى الرد بحلول {date}.',
      thankYou: 'شكراً - تم إرسال ردك ({response}).',
      closed: 'مغلق',
      backToWorkspace: 'رجوع',
    },

    // Form page
    form: {
      titleLabel: 'العنوان',
      thankYou: 'شكراً - تم إرسال إجاباتك.',
      fillIn: 'يرجى ملء: {fields}.',
      send: 'إرسال',
      sending: 'جارٍ الإرسال…',
      choose: 'اختر…',
      backToWorkspace: 'رجوع',
    },

    // Quote/RFQ page
    quote: {
      sendQuote: 'إرسال عرض سعر',
      title: 'العنوان',
      total: 'الإجمالي',
      currency: 'العملة',
      currencyPlaceholder: 'USD',
      notes: 'الملاحظات',
      send: 'إرسال عرض السعر',
      sending: 'جارٍ الإرسال…',
      titleRequired: 'يرجى إعطاء عرض السعر الخاص بك عنواناً.',
      thankYou: 'شكراً - تم إرسال عرض السعر الخاص بك{reference}.',
      backToWorkspace: 'رجوع',
    },

    // Session ended / Not found
    ended: {
      title: 'انتهت جلستك',
      description: 'لأسباب أمنية، يتم إغلاق الوصول عند تسجيل الخروج أو بعد فترة من عدم النشاط. يرجى فتح رابط الدعوة الخاص بك مرة أخرى.',
      lostLink: 'إذا كان الرابط لم يعد يعمل، اطلب دعوة جديدة.',
    },

    notFound: {
      title: 'لم نتمكن من العثور على تلك الصفحة',
      goBack: 'رجوع',
    },

    // Error messages
    errors: {
      somethingWrong: 'حدث خطأ ما من جانبنا.',
      couldNotAccept: 'لم نتمكن من فتح وصولك الآن.',
      couldNotOpen: 'لم نتمكن من فتح ذلك.',
      couldNotOpenDesk: 'لم نتمكن من فتح ذلك القسم.',
      couldNotSend: 'لم نتمكن من إرسال ذلك الآن.',
      couldNotDownload: 'لم يتمكن هذا التحميل من البدء.',
      couldNotConfirm: 'لم يتمكن هذا من المتابعة.',
      couldNotRespond: 'لم يتمكن ذلك من الإرسال.',
      couldNotSubmitForm: 'لم يتمكن ذلك من الإرسال.',
      couldNotSubmitQuote: 'لم يتمكن ذلك من الإرسال.',
    },
  },
};
